import { Linkedin } from "lucide-react";
import woozyLogo from "@/assets/woozyai-logo-white-160.png";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageProvider";

const Footer = () => {
  const currentYear = 2026;
  const { t } = useLanguage();

  const platformLinks = [
    { to: "/how-it-works", label: t("How It Works") },
    { to: "/platform", label: t("Capabilities") },
    { to: "/solutions/asset-owners", label: t("Solar Revenue Recovery") },
    { to: "/solutions/storage", label: t("Storage Optimization") },
    { to: "/solutions/carbon-value", label: t("Carbon Value Recovery") },
    { to: "/solutions/esg", label: t("ESG Intelligence") },
  ];

  const resourceLinks = [
    { to: "/validation-methodology", label: t("Validation & Methodology") },
    { to: "/integrations", label: t("Integrations") },
    { to: "/resources", label: t("Resources & FAQs") },
    { to: "/project-capital", label: t("Project Capital") },
    { to: "/about", label: t("About Us") },
    { to: "/contact", label: t("Contact") },
  ];

  const legalLinks = [
    { to: "/privacy-policy", label: t("Privacy Policy") },
    { to: "/terms-of-service", label: t("Terms of Service") },
    { to: "/cookie-policy", label: t("Cookie Policy") },
    { to: "/accessibility-statement", label: t("Accessibility Statement") },
  ];

  return (
    <footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <LocalizedLink to="/" className="flex items-center mb-4 group rounded-md" aria-label={t("WoozyAI home")}>
              <img 
                src={woozyLogo} 
                alt={t("WoozyAI home")}
                width="160"
                height="134"
                loading="lazy"
                decoding="async"
                className="h-24 w-auto group-hover:drop-shadow-[0_0_8px_rgba(0,240,255,0.4)] transition-all" 
              />
            </LocalizedLink>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              {t(
                "The AI revenue-recovery and autonomous performance layer for solar and storage portfolios - from utility-scale farms to distributed commercial rooftops.",
              )}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://www.linkedin.com/company/woozyai/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-md bg-muted/30 hover:bg-muted hover:text-primary transition-colors"
                aria-label={t("WoozyAI on LinkedIn opens in a new tab")}
              >
                <Linkedin size={18} aria-hidden="true" />
              </a>
              <LanguageSwitcher />
            </div>
          </div>

          {/* Platform */}
          <div>
            <h2 className="font-display font-semibold text-sm mb-4">{t("Platform")}</h2>
            <ul className="space-y-2.5">
              {platformLinks.map((link) => (
                <li key={link.to}>
                  <LocalizedLink to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h2 className="font-display font-semibold text-sm mb-4">{t("Resources")}</h2>
            <ul className="space-y-2.5">
              {resourceLinks.map((link) => (
                <li key={link.to}>
                  <LocalizedLink to={link.to} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {currentYear} WoozyAI. {t("All rights reserved.")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {legalLinks.map((link) => (
              <LocalizedLink
                key={link.to}
                to={link.to}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </LocalizedLink>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
